import {
  Component,
  inject,
  effect,
  ChangeDetectorRef,
  computed,
  signal,
  untracked,
} from '@angular/core'
import { FilterExpression, KENDO_FILTER } from '@progress/kendo-angular-filter'
import { CompositeFilterDescriptor } from '@progress/kendo-data-query'

import { SupportedTools, toolFactory } from '@cyborg-monorepo/hunter/matchboi'
import {
  TypographyComponent,
  ButtonComponent,
  DialogService,
  MessageComponent,
  MonacoComponent,
  IconComponent,
  SnackbarService,
} from '@hyperkit'

import { QueryEditorService, TuningAPIService } from '@hunt/services'

import { supportedPivot } from '../../../../../helpers/utils'
import { NavigationService } from '../../../services/navigation.service'
import { PackageViewService } from '../../../services/reader/package-view.service'
import { QueryViewService } from '../../../services/reader/query-view.service'
import { SessionService } from '../../../services/session.service'

import { fromAst, toAst } from './utils/ast'
import { pickEditor, pruneForTool } from './utils/tuning-utils'
import { AstNode } from './utils/types'

@Component({
  selector: 'query-tuning',
  standalone: true,
  imports: [
    TypographyComponent,
    ButtonComponent,
    KENDO_FILTER,
    IconComponent,
    MessageComponent,
    MonacoComponent,
  ],
  templateUrl: './query-tuning.component.html',
  styleUrl: './query-tuning.component.scss',
})
export class QueryTuningComponent {
  private readonly cdr = inject(ChangeDetectorRef)
  public readonly queryView = inject(QueryViewService)
  public readonly nav = inject(NavigationService)
  private readonly dialog = inject(DialogService)
  private readonly session = inject(SessionService)
  public readonly tuningAPIService = inject(TuningAPIService)
  public readonly pkgView = inject(PackageViewService)
  public readonly snack = inject(SnackbarService)

  tool = signal<SupportedTools>(this.queryView.selectedToolType())

  // Build the field list for the current tool (preserve value on tool change)
  filters = signal<FilterExpression[]>([])
  filterValue = signal<CompositeFilterDescriptor>({ logic: 'and', filters: [] })

  // Render outputs
  astJSON = signal<string>('{}')
  emitted = signal<string>('')

  readonly loaded = computed(
    () => !this.session.loading() && this.session.activeHunt() !== null,
  )

  /** read‐only view of the current query/pivot node */
  private readonly selectedQuery = inject(QueryViewService).selectedQuery
  readonly toolType = inject(QueryViewService).selectedToolType
  readonly supportedPivot = supportedPivot(this.toolType())
  readonly huntCategory = inject(PackageViewService).huntCategory
  public readonly queryEditor = inject(QueryEditorService)
  tuningText = signal<string>('')

  /** which pivotTab are we on (overview|deployment|emulation) */
  readonly selectedTab = this.nav.pivotTab

  constructor() {
    this.tuningAPIService
      .getFieldsForTool(this.tool(), this.pkgView.huntCategory())
      .subscribe((data) => {
        this.filters.set(
          data.map((k) => ({ field: k, title: k, editor: pickEditor(k) })),
        )
      })

    effect(() => {
      const tool = this.queryView.selectedToolType()

      this.tuningText.set(toolFactory(tool).tuningText)

      this.tuningAPIService
        .getFieldsForTool(tool, this.pkgView.huntCategory())
        .subscribe((data) => {
          this.filters.set(
            data.map((k) => ({ field: k, title: k, editor: pickEditor(k) })),
          )
        })
    })

    effect(() => {
      const queryId = this.queryView.selectedQueryId()
      const query = untracked(() => this.queryView.selectedQuery())

      if (query.tuning) {
        const initialFilters = fromAst(
          query.tuning as AstNode,
        ) as CompositeFilterDescriptor
        this.filterValue.set(initialFilters)
      } else {
        // no tuning yet, set to default empty filter
        this.filterValue.set({ logic: 'and', filters: [] })
      }
    })

    effect(() => {
      const v = this.filterValue()
      const toolId = untracked(() => this.queryView.selectedToolId().id)
      const category = untracked(() => this.queryView.selectedQuery().category)

      try {
        const ast = toAst(v)
        this.astJSON.set(JSON.stringify(ast, null, 2))

        this.tuningAPIService
          .generateTuningContentFromFilter(ast, toolId, category)
          .subscribe((tuning) => {
            this.emitted.set(tuning)
            this.queryEditor.updateQueryContent({ tuning: ast })
          })
      } catch (e: any) {
        this.astJSON.set(`ERROR: ${e?.message ?? e}`)
        this.emitted.set('')
      }
    })
  }

  onValueChange(v: CompositeFilterDescriptor) {
    this.filterValue.set(v)
  }

  toggleTuning(): void {
    this.nav.mergeQueryParams({ showTuning: !this.nav.showTuning() })
  }

  copyTuning(): void {
    const q = this.emitted() ?? ''
    navigator.clipboard.writeText(q)
    this.snack.show('Tuning query clause copied')
  }
}
